// src/admin/pages/AdminEmailEditor.js
import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";

import {
  getAdminCoupons,
  createAdminCoupon,
  updateAdminCoupon,
} from "../../services/adminCoupons.api";

function AdminEmailEditor() {
  const { key } = useParams(); // key = coupon id OR "new"
  const navigate = useNavigate();
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isNew = key === "new";

  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",
    discount_value: "",
    max_uses: "",
    is_active: true,
  });

  const TEXT = {
    EN: {
      back: "Back",
      titleAdd: "Add Promo Code",
      titleEdit: "Edit Promo Code",
      code: "Promo Code",
      type: "Discount Type",
      value: "Discount Value",
      maxUses: "Max Uses (optional)",
      active: "Active",
      save: "Save Promo Code",
    },
    AR: {
      back: "رجوع",
      titleAdd: "إضافة كود خصم",
      titleEdit: "تعديل كود خصم",
      code: "كود الخصم",
      type: "نوع الخصم",
      value: "قيمة الخصم",
      maxUses: "الحد الأقصى للاستخدام (اختياري)",
      active: "مفعل",
      save: "حفظ كود الخصم",
    },
  }[lang];

  // 🔄 Load coupon if editing
  useEffect(() => {
    if (isNew) return;

    async function loadCoupon() {
      setLoading(true);
      const list = await getAdminCoupons();
      const c = list.find((x) => String(x.id) === key);

      if (c) {
        setForm({
          code: c.code,
          discount_type: c.discount_type,
          discount_value: c.discount_value,
          max_uses: c.max_uses ?? "",
          is_active: !!c.is_active,
        });
      }

      setLoading(false);
    }

    loadCoupon();
  }, [key, isNew]);

  const handleSave = async () => {
    if (!form.code || !form.discount_value) return;

    if (isNew) {
      await createAdminCoupon(form);
    } else {
      await updateAdminCoupon(key, form);
    }

    navigate("/admin/email-templates");
  };

  return (
    <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className="flex-1 lg:ml-64 transition-all duration-300">
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6 max-w-3xl mx-auto animate-fadeSlide">

          {/* BACK */}
          <button
            className="text-cyan-700 flex items-center gap-2 mb-4 hover:text-cyan-900"
            onClick={() => navigate("/admin/email-templates")}
          >
            <FaArrowLeft /> {TEXT.back}
          </button>

          {/* TITLE */}
          <h1 className="text-2xl font-bold text-cyan-700 mb-6">
            {isNew ? TEXT.titleAdd : TEXT.titleEdit}
          </h1>

          {/* FORM */}
          <div className="bg-white shadow-xl rounded-2xl p-6 border space-y-5">

            {/* CODE */}
            <div>
              <label className="block font-semibold mb-2">
                {TEXT.code}
              </label>
              <input
                className="w-full p-3 border rounded-xl"
                value={form.code}
                onChange={(e) =>
                  setForm({ ...form, code: e.target.value.toUpperCase() })
                }
              />
            </div>

            {/* TYPE */}
            <div>
              <label className="block font-semibold mb-2">
                {TEXT.type}
              </label>
              <select
                className="w-full p-3 border rounded-xl"
                value={form.discount_type}
                onChange={(e) =>
                  setForm({ ...form, discount_type: e.target.value })
                }
              >
                <option value="percent">Percent (%)</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            {/* VALUE */}
            <div>
              <label className="block font-semibold mb-2">
                {TEXT.value}
              </label>
              <input
                type="number"
                className="w-full p-3 border rounded-xl"
                value={form.discount_value}
                onChange={(e) =>
                  setForm({ ...form, discount_value: e.target.value })
                }
              />
            </div>

            {/* MAX USES */}
            <div>
              <label className="block font-semibold mb-2">
                {TEXT.maxUses}
              </label>
              <input
                type="number"
                className="w-full p-3 border rounded-xl"
                value={form.max_uses}
                onChange={(e) =>
                  setForm({ ...form, max_uses: e.target.value })
                }
              />
            </div>

            {/* ACTIVE */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />
              <span className="font-semibold">{TEXT.active}</span>
            </div>

            {/* SAVE */}
            <button
              disabled={loading}
              className="px-6 py-3 bg-cyan-600 text-white rounded-xl flex items-center gap-2 hover:bg-cyan-700"
              onClick={handleSave}
            >
              <FaSave /> {TEXT.save}
            </button>
          </div>

        </main>
      </div>
    </div>
  );
}

export default AdminEmailEditor;